import { Controller } from "@hotwired/stimulus"
import { Chart } from "chart.js/auto"
import 'chartjs-adapter-date-fns'
import colors from 'tailwindcss/colors'

export default class extends Controller {
  static values = {
    data: Array,
    options: Object
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  initializeChart() {
    const datasets = this.buildDatasets()
    const timeScale = this.optionsValue.time_scale

    this.chart = new Chart(this.element, {
      type: 'line',
      data: {
        datasets
      },
      options: {
        responsive: true,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 6,
              boxHeight: 6
            },
          }
        },
        scales: {
          x: {
            type: 'time',
            time: { unit: timeScale },
            grid: { display: false }
          },
          ...this.buildYScales()
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: context => {
                const date = new Date(context[0].raw.x).toLocaleString([], {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })

                return date
              }
            }
          },
          legend: {
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 6,
              boxHeight: 6
            },
          }
        }
      }
    })
  }

  buildDatasets() {
    const singleScale = this.optionsValue.single_scale
    return this.dataValue.map((series, index) => ({
      label: series.name,
      data: series.data.map(([date, value]) => ({
        x: new Date(date),
        y: value
      })),
      borderColor: series.color || this.colors[index],
      backgroundColor: series.color || this.colors[index],
      tension: 0.4,
      yAxisID: singleScale ? "y" : (index === 0 ? "y" : `y${index}`)
    }))
  }

  buildYScales() {
    if (this.optionsValue.single_scale) {
      return {
        y: {
          position: 'left',
          title: {
            display: true,
            text: this.optionsValue.y_title || 'Score'
          },
          grid: {
            drawOnChartArea: true
          }
        }
      }
    }

    const scales = {}
    this.dataValue.forEach((series, index) => {
      const axisId = index === 0 ? "y" : `y${index}`
      scales[axisId] = {
        position: index === 0 ? 'left' : 'right',
        title: {
          display: true,
          text: series.name
        },
        grid: {
          drawOnChartArea: index === 0 ? true : false
        },
        beginAtZero: this.optionsValue.beginAtZero ?? false,
        min: this.optionsValue.min ?? null
      }
    })
    return scales
  }

  get colors() {
    return [
      colors.indigo[600],
      colors.purple[600],
      colors.orange[600],
      colors.yellow[600],
      colors.green[600],
      colors.cyan[600],
      colors.blue[600],
      colors.fuchsia[600],
      colors.rose[600],
      colors.red[300]
    ]
  }
}
